import { IPokerUserSettings } from '../Core/State/IPokerUserSettings';
import { SocketProvider } from '../Core/Socket/SocketProvider';
import { notificationFrequencyManager } from './NotificationFrequencyManager';
import { setVolume, setMuted } from './AudioUtils';
import { changeLanguage, supportedLanguages, getCurrentLanguage } from '../i18n';

type SettingKey = keyof IPokerUserSettings;

interface SettingConfig<T = any> {
    key: SettingKey;
    defaultValue: T;
    validate: (value: T) => boolean;
    normalize: (value: T) => T;
    onUpdate?: (value: T) => void;
    debounceMs?: number;
}

export class UserSettingsManager {
    private connection: SocketProvider | null;
    private currentSettings: IPokerUserSettings;
    private debounceTimers: Map<SettingKey, NodeJS.Timeout> = new Map();
    private pendingUpdates: Map<SettingKey, any> = new Map();

    private settingsConfig: SettingConfig[] = [
        {
            key: 'clientVolume',
            defaultValue: 0.7,
            validate: (value: number) => value >= 0 && value <= 1,
            normalize: (value: number) => Math.round(value * 10) / 10,
            onUpdate: (value: number) => {
                setVolume(value);
                setMuted(value === 0);
            },
            debounceMs: 500
        },
        {
            key: 'notificationFrequency',
            defaultValue: 1,
            validate: (value: number) => value >= 0 && value <= 1,
            normalize: (value: number) => Math.round(value * 10) / 10,
            onUpdate: (value: number) => {
                notificationFrequencyManager.setUserFrequency(value);
            }
        },
        {
            key: 'language',
            defaultValue: 'en',
            validate: (value: string) => supportedLanguages.some(lang => lang.code === value),
            normalize: (value: string) => value,
            onUpdate: (value: string) => {
                // Only change if different from current i18n language to avoid redundant calls
                if (getCurrentLanguage() !== value) {
                    changeLanguage(value as any).catch(err => console.error('Failed to change language:', err));
                }
            }
        }
    ];

    constructor(connection: SocketProvider | null) {
        this.connection = connection;
        this.currentSettings = this.getDefaultSettings();
    }

    public setConnection(connection: SocketProvider | null): void {
        this.connection = connection;
    }

    public getDefaultSettings(): IPokerUserSettings {
        const defaults = {} as IPokerUserSettings;
        this.settingsConfig.forEach(config => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (defaults as any)[config.key] = config.defaultValue;
        });
        return defaults;
    }

    public loadSettings(settings: Partial<IPokerUserSettings>, isInitialLogin: boolean = false): void {
        const validatedSettings = this.validateAndNormalizeSettings(settings);

        // On INITIAL login only: Check if user selected a different language on login page
        // If so, prioritize the login page selection and sync to server
        if (isInitialLogin && validatedSettings.language) {
            const currentI18nLanguage = getCurrentLanguage();
            const serverLanguage = validatedSettings.language;

            if (currentI18nLanguage !== serverLanguage) {
                // User selected a different language on login page - update server to match
                validatedSettings.language = currentI18nLanguage;
                // Send update to server (after a short delay to ensure connection is ready)
                setTimeout(() => {
                    this.updateSetting('language', currentI18nLanguage, { immediate: true });
                }, 100);
            }
        }

        this.currentSettings = { ...this.currentSettings, ...validatedSettings };
        this.applySettingsLocally(validatedSettings);
    }

    public updateSetting<K extends SettingKey>(
        key: K,
        value: IPokerUserSettings[K],
        options: { silent?: boolean; immediate?: boolean } = {}
    ): void {
        const config = this.settingsConfig.find(c => c.key === key);
        if (!config) {
            console.error(`Unknown setting key: ${key}`);
            return;
        }

        if (!config.validate(value)) {
            console.error(`Invalid value for ${key}:`, value);
            return;
        }

        const normalizedValue = config.normalize(value);
        this.currentSettings[key] = normalizedValue;

        if (config.onUpdate) {
            config.onUpdate(normalizedValue);
        }

        this.pendingUpdates.set(key, normalizedValue);

        if (options.immediate) {
            this.sendPendingUpdates();
        } else {
            this.debouncedSend(key, config.debounceMs);
        }
    }

    public updateMultipleSettings(
        settings: Partial<IPokerUserSettings>
    ): void {
        const validatedSettings = this.validateAndNormalizeSettings(settings);

        Object.entries(validatedSettings).forEach(([key, value]) => {
            const settingKey = key as SettingKey;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.currentSettings as any)[settingKey] = value;

            const config = this.settingsConfig.find(c => c.key === settingKey);
            if (config?.onUpdate) {
                config.onUpdate(value);
            }

            this.pendingUpdates.set(settingKey, value);
        });

        this.sendPendingUpdates();
    }

    public getCurrentSettings(): IPokerUserSettings {
        return { ...this.currentSettings };
    }

    public getSetting<K extends SettingKey>(key: K): IPokerUserSettings[K] {
        return this.currentSettings[key];
    }

    private validateAndNormalizeSettings(settings: Partial<IPokerUserSettings>): Partial<IPokerUserSettings> {
        const validated: Partial<IPokerUserSettings> = {};

        Object.entries(settings).forEach(([key, value]) => {
            const settingKey = key as SettingKey;
            const config = this.settingsConfig.find(c => c.key === settingKey);

            if (config && config.validate(value)) {
                validated[settingKey] = config.normalize(value);
            }
        });

        return validated;
    }

    private applySettingsLocally(settings: Partial<IPokerUserSettings>): void {
        Object.entries(settings).forEach(([key, value]) => {
            const settingKey = key as SettingKey;
            const config = this.settingsConfig.find(c => c.key === settingKey);
            
            if (config?.onUpdate) {
                config.onUpdate(value);
            }
        });
    }

    private debouncedSend(key: SettingKey, debounceMs: number = 0): void {
        const existingTimer = this.debounceTimers.get(key);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        if (debounceMs > 0) {
            const timer = setTimeout(() => {
                this.sendPendingUpdates();
            }, debounceMs);
            this.debounceTimers.set(key, timer);
        } else {
            this.sendPendingUpdates();
        }
    }

    private sendPendingUpdates(): void {
        if (this.pendingUpdates.size === 0) return;

        const updates: Partial<IPokerUserSettings> = {};
        this.pendingUpdates.forEach((value, key) => {
            updates[key] = value;
        });

        this.connection?.send('player', 'updateUserSettings', updates);
        
        this.pendingUpdates.clear();
        this.debounceTimers.forEach(timer => clearTimeout(timer));
        this.debounceTimers.clear();
    }

    public handleServerUpdate(data: { success: boolean; pokerUserSettings?: IPokerUserSettings; keysChanged?: string[] }): {
        shouldShowNotification: boolean;
        settings: IPokerUserSettings | null;
    } {
        if (!data.success || !data.pokerUserSettings) {
            return { shouldShowNotification: false, settings: null };
        }

        this.loadSettings(data.pokerUserSettings);

        const keysChanged = data.keysChanged || [];
        const silentSettings = ['clientVolume', 'language'];
        const isOnlySilentChange = keysChanged.length > 0 && keysChanged.every(key => silentSettings.includes(key));

        return {
            shouldShowNotification: !isOnlySilentChange,
            settings: data.pokerUserSettings
        };
    }

    public getNotificationFrequencyUIValue(): number {
        return (this.currentSettings.notificationFrequency ?? 1) * 100;
    }

    public setNotificationFrequencyFromUIValue(uiValue: number): void {
        const normalizedValue = uiValue / 100;
        this.currentSettings.notificationFrequency = normalizedValue;
    }

    public getNotificationFrequencyDescription(uiValue: number): string {
        if (uiValue === 0) {
            return 'Off - No notifications';
        } else if (uiValue === 10) {
            return 'Every 60 minutes';
        } else if (uiValue === 20) {
            return 'Every 50 minutes';
        } else if (uiValue === 30) {
            return 'Every 40 minutes';
        } else if (uiValue === 40) {
            return 'Every 30 minutes';
        } else if (uiValue === 50) {
            return 'Every 20 minutes';
        } else if (uiValue === 60) {
            return 'Every 15 minutes';
        } else if (uiValue === 70) {
            return 'Every 10 minutes';
        } else if (uiValue === 80) {
            return 'Every 5 minutes';
        } else if (uiValue === 90) {
            return 'Every 2 minutes';
        } else if (uiValue === 100) {
            return 'All notifications (unlimited)';
        }
        return 'Custom';
    }

    public getNotificationFrequencySliderConfig() {
        return {
            min: 0,
            max: 100,
            step: 10,
            marks: [
                { value: 0, label: '0%' },
                { value: 20, label: '20%' },
                { value: 40, label: '40%' },
                { value: 60, label: '60%' },
                { value: 80, label: '80%' },
                { value: 100, label: '100%' }
            ]
        };
    }
}

